/**
 * L'erreur unique du client (D21, « erreurs normalisées ») : `status` porte le code HTTP pour
 * que l'écran distingue un refus (401) d'une limite de tentatives (429), et `cause` porte le
 * détail (`ZodError` pour une réponse hors contrat, erreur de parsing JSON…).
 *
 * `body` porte le corps JSON de la réponse d'erreur quand le serveur en a produit un lisible :
 * le statut seul est parfois ambigu — le 409 du dépôt terrain dit en code machine si le relevé
 * est déjà rangé ou refusé, et jeter cette réponse coûterait un relevé (M21).
 */
export class ApiError extends Error {
    status;
    body;
    constructor(message, status, options) {
        super(message, options);
        this.status = status;
        this.name = 'ApiError';
        this.body = options?.body;
    }
}
/**
 * Un fichier **coupé en route** : le serveur a répondu `2xx` et commencé à l'envoyer, puis la
 * connexion a cassé — il l'a coupée lui-même quand l'archive ne pouvait pas s'achever (#673).
 *
 * ⚠️ À ne pas confondre avec un serveur qui n'a pas répondu : ici, l'appel **a abouti**. Ce qu'il
 * écrit côté serveur — la trace d'une livraison (M26) — est fait ; seul le fichier manque.
 */
export class DownloadInterruptedError extends Error {
    constructor(request, options) {
        super(`${request} → téléchargement coupé en route`, options);
        this.name = 'DownloadInterruptedError';
    }
}
/** Les octets d'un fichier dont l'en-tête est arrivé : une lecture qui casse est une coupure. */
async function bodyOf(response, request) {
    try {
        return await response.blob();
    }
    catch (cause) {
        throw new DownloadInterruptedError(request, { cause });
    }
}
/**
 * Le nom de fichier annoncé par `Content-Disposition`.
 *
 * ⚠️ **Volontairement étroit** : la seule forme reconnue est `filename="…"`, celle que l'API
 * produit — un nom ASCII, entre guillemets ([D26](../../../docs/ARCHITECTURE.md#d26)). La forme
 * RFC 5987 (`filename*=UTF-8''…`) n'est **pas** relue, et ce n'est pas un oubli : personne ne
 * l'émet ici, et un décodage jamais exercé serait un décodage jamais vérifié. Le jour où un nom
 * accentué serait utile, c'est ce couple — producteur et lecteur — qui change ensemble.
 *
 * `null` plutôt qu'un nom inventé : l'appelant sait mieux comment nommer ce qu'il a demandé.
 */
export function attachmentFilename(disposition) {
    return disposition === null ? null : (/filename="([^"]+)"/i.exec(disposition)?.[1] ?? null);
}
/** L'en-tête d'identité du terrain : le jeton d'accès de 15 minutes (M23). */
export function bearer(accessToken) {
    return { authorization: `Bearer ${accessToken}` };
}
/**
 * Le transport partagé par toutes les familles d'appels du client.
 *
 * Extrait de `createApiClient` parce qu'`auth` et `sync` n'ont ni le même corps ni le même
 * porteur d'identité — cookie d'un côté, `Authorization` de l'autre (D3) — mais **exactement**
 * la même façon de traiter un échec : un `ApiError` qui porte le statut, et une réponse hors
 * contrat qui échoue ici plutôt que trois écrans plus loin.
 *
 * `credentials: 'include'` sur chaque appel : le refresh du back-office ne voyage qu'en cookie
 * httpOnly (D3). Le terrain, lui, passe le sien dans le corps — les deux canaux traversent le
 * même transport.
 */
export function createHttp({ baseUrl, fetch = globalThis.fetch, onCall }) {
    const root = baseUrl.replace(/\/$/, '');
    function report(call) {
        try {
            onCall?.(call);
        }
        catch {
            // L'observateur est de la télémétrie : son échec ne coûte jamais l'appel observé.
        }
    }
    async function send(method, path, init = {}) {
        const startedAt = performance.now();
        let response;
        try {
            response = await fetch(`${root}${path}`, { method, credentials: 'include', ...init });
        }
        catch (failure) {
            // `status: 0` — la convention du navigateur pour « le réseau n'a pas répondu ».
            report({ method, path, status: 0, ok: false, durationMs: performance.now() - startedAt });
            throw failure;
        }
        report({
            method,
            path,
            status: response.status,
            ok: response.ok,
            durationMs: performance.now() - startedAt,
        });
        if (!response.ok) {
            // Un corps illisible (vide, HTML d'un proxy…) ne vaut pas un échec de plus : `body` reste
            // simplement absent, et l'appelant retombe sur son défaut sûr.
            const body = await response.json().catch(() => undefined);
            throw new ApiError(`${method} ${path} → ${response.status}`, response.status, { body });
        }
        return response;
    }
    async function parse(method, path, response, schema) {
        let payload;
        try {
            payload = await response.json();
        }
        catch (cause) {
            throw new ApiError(`${method} ${path} : réponse illisible`, response.status, { cause });
        }
        const parsed = schema.safeParse(payload);
        if (!parsed.success) {
            throw new ApiError(`${method} ${path} : réponse hors contrat`, response.status, {
                cause: parsed.error,
            });
        }
        return parsed.data;
    }
    async function get(path, schema, headers) {
        return parse('GET', path, await send('GET', path, { headers }), schema);
    }
    /** Une écriture à corps JSON : `POST`, `PATCH` et `PUT` ne diffèrent que par le verbe. */
    function writeJson(method, path, body, headers) {
        return send(method, path, {
            headers: { 'content-type': 'application/json', ...headers },
            body: JSON.stringify(body),
        });
    }
    function post(path, body, headers) {
        return writeJson('POST', path, body, headers);
    }
    async function postJson(path, body, schema, headers) {
        return parse('POST', path, await post(path, body, headers), schema);
    }
    /**
     * Un `POST` dont le **corps** est un document et la **réponse** du JSON — l'inverse de
     * `postFile`.
     *
     * ⚠️ **Les octets partent tels quels, jamais en `multipart/form-data`.** La route d'extraction
     * est montée sur `express.raw` pour le seul type qu'elle accepte : un formulaire encodé y
     * laisserait `req.body` vide, et le serveur répondrait « corps vide » sur un document
     * parfaitement lisible. L'enveloppe multipart n'apporterait rien ici — il n'y a qu'une pièce,
     * et son nom n'intéresse personne, le document n'étant pas conservé.
     *
     * Le type déclaré est celui du blob, sauf si l'appelant en impose un : un fichier dont le
     * poste n'a pas deviné le type arrive avec un `type` vide, et l'en-tête absent vaudrait un
     * refus que l'utilisateur ne peut pas corriger.
     */
    async function postBinaryJson(path, document, schema, headers) {
        const response = await send('POST', path, {
            headers: { 'content-type': document.type, ...headers },
            body: document,
        });
        return parse('POST', path, response, schema);
    }
    /**
     * Un retrait dont la réponse porte du JSON — la composition de l'équipe après coup, par exemple.
     *
     * ⚠️ Pas de `204` toléré ici : le schéma est obligatoire, donc un corps l'est aussi. C'est
     * délibéré — un retrait qui ne rend rien oblige l'écran à relire la liste entière pour savoir ce
     * que son geste a produit, et deux lectures peuvent se contredire.
     */
    async function deleteJson(path, schema, headers) {
        return parse('DELETE', path, await send('DELETE', path, { headers }), schema);
    }
    /**
     * La correction partielle d'une ressource — le corps ne porte **que ce qui change**. C'est le
     * verbe par défaut des écritures d'ici : presque rien dans cette API ne se remplace en entier,
     * et un `PUT` obligerait l'appelant à renvoyer des champs qu'on lui refuse (cf.
     * `updateUserSchema`, M6). L'exception est `putJson`, juste en dessous.
     */
    async function patchJson(path, body, schema, headers) {
        return parse('PATCH', path, await writeJson('PATCH', path, body, headers), schema);
    }
    /**
     * Le remplacement **entier** d'une ressource, corps et réponse en JSON.
     *
     * ⚠️ **L'exception, et sa condition d'existence** : la règle du rapport d'une cible se remplace
     * en bloc — l'écran « Gestion du rapport PDF » envoie l'ensemble des blocs retirés, pas un
     * delta —, et `PUT` est ce qui rend l'écriture **idempotente** : rejouée, elle ne crée pas une
     * seconde règle pour la même cible. La couverture d'une équipe est dans le même cas (la liste
     * entière de ses communes). Une écriture qui ne remplace pas tout reste un `patchJson`.
     */
    async function putJson(path, body, schema, headers) {
        return parse('PUT', path, await writeJson('PUT', path, body, headers), schema);
    }
    /**
     * Un `PUT` dont le corps est **binaire** : le blob part tel quel, avec son propre type. Ne
     * jamais le sérialiser en base64 — c'est un tiers de volume en plus sur la 4G d'un chantier,
     * pour une photo qu'IndexedDB sait déjà porter en `Blob`.
     */
    async function putBinary(path, blob, headers) {
        await send('PUT', path, { headers: { 'content-type': blob.type, ...headers }, body: blob });
    }
    /**
     * Un `POST` en **`multipart/form-data`** qui répond du JSON — un fichier et ses champs.
     *
     * ⚠️ **Aucun `content-type` posé à la main** : c'est `fetch` qui l'écrit, avec la frontière
     * (`boundary`) qu'il vient de tirer. L'écrire ici le priverait de cette frontière, et le serveur
     * ne saurait plus découper le corps.
     */
    async function postFormJson(path, form, schema, headers) {
        return parse('POST', path, await send('POST', path, { headers, body: form }), schema);
    }
    /** Un `POST` sans corps qui ne rend rien (`202`, `204`) : un geste, pas une ressource. */
    async function postEmpty(path, headers) {
        await send('POST', path, { headers });
    }
    /** Un `DELETE` qui ne rend rien (`204`). */
    async function deleteEmpty(path, headers) {
        await send('DELETE', path, { headers });
    }
    /**
     * Un `GET` dont la réponse est **binaire** : les octets sortent en `Blob`, avec le type que le
     * serveur a déclaré.
     *
     * ⚠️ Aucun schéma, donc aucune validation — et c'est le seul appel du client dans ce cas. Il n'y
     * a rien à valider dans une photo : le contrat porte sur la **référence** du média, vérifiée là
     * où elle voyage (`submittedInspectionSchema`). Un `Blob` vide ou d'un type inattendu se voit à
     * l'affichage, et le contrôler ici demanderait de deviner ce qu'un média légitime peut être.
     *
     * ⚠️ **Un `Blob` et non une URL.** Le jeton d'accès du back-office vit en mémoire (D3), donc
     * `<img src="…">` ne peut pas le présenter : le navigateur n'attache aucun en-tête à une
     * requête d'image. L'appelant fabrique donc son `URL.createObjectURL` et le révoque — à charge
     * pour lui, une URL d'objet non révoquée retient les octets jusqu'au rechargement de la page.
     * L'URL pré-signée règlerait les deux en une fois, et c'est le `🟡` de D5.
     */
    async function getBinary(path, headers) {
        return (await send('GET', path, { headers })).blob();
    }
    /**
     * Un `GET` dont la **réponse** est un fichier : l'appel lit côté serveur, et ce qu'il rend
     * n'est pas une ressource JSON mais des octets à enregistrer.
     *
     * ⚠️ Même contrat que `postFile` — blob + nom relu dans `Content-Disposition` — pour un
     * verbe différent. L'export du tableau des demandes n'écrit rien, donc `POST` mentirait ;
     * `getBinary` ne rend pas le nom, donc le client retomberait en silence sur un défaut.
     */
    async function getFile(path, headers) {
        const response = await send('GET', path, { headers });
        return {
            blob: await bodyOf(response, `GET ${path}`),
            filename: attachmentFilename(response.headers.get('content-disposition')),
        };
    }
    /**
     * Un `POST` dont la **réponse** est un fichier : l'appel écrit côté serveur, et ce qu'il rend
     * n'est pas une ressource mais des octets à enregistrer.
     *
     * ⚠️ Aucun schéma, donc aucune validation — comme `getBinary`, et pour la même raison : il n'y
     * a rien à valider dans un CSV. Ce qui est vérifié, c'est ce qui *décrit* le fichier — son nom,
     * relu dans l'en-tête plutôt que réinventé par l'appelant.
     *
     * ⚠️ **Un `Blob` et non une URL.** Le jeton d'accès du back-office vit en mémoire (D3) : une
     * navigation vers l'URL du fichier partirait sans en-tête d'autorisation. C'est donc à
     * l'appelant de fabriquer son `URL.createObjectURL` **et de le révoquer** — un export de lot
     * n'est pas petit, et une URL d'objet non révoquée retient les octets jusqu'au rechargement.
     */
    async function postFile(path, body, headers) {
        const response = await post(path, body, headers);
        return {
            blob: await bodyOf(response, `POST ${path}`),
            filename: attachmentFilename(response.headers.get('content-disposition')),
        };
    }
    return {
        deleteJson,
        deleteEmpty,
        get,
        getBinary,
        getFile,
        patchJson,
        post,
        postBinaryJson,
        postEmpty,
        postFile,
        postFormJson,
        postJson,
        putBinary,
        putJson,
    };
}
//# sourceMappingURL=http.js.map